<template>
  <!-- #16 — Schicht-Produktions-Protokoll (append-only): produzierte Stückzahl
       je Maschine / Schicht / Tag erfassen und revisionssicher auflisten. -->
  <div class="shift-panel">
    <header class="sp-head">
      <i class="fas fa-layer-group"></i>
      <span>Schicht-Produktion</span>
      <small class="sp-totals">
        Heute: <strong>{{ fmt(producedToday) }}</strong>
        &middot; Gesamt: <strong>{{ fmt(producedTotal) }}</strong>
      </small>
    </header>

    <!-- Erfassungsmaske (PC / Schichtleiter) -->
    <form class="sp-form" @submit.prevent="submit">
      <div class="sp-field">
        <label>Datum</label>
        <input type="date" v-model="form.datum" required />
      </div>
      <div class="sp-field">
        <label>Schicht</label>
        <select v-model="form.schicht">
          <option :value="null">1-Schicht</option>
          <option :value="1">1 (06–14)</option>
          <option :value="2">2 (14–24)</option>
        </select>
      </div>
      <div class="sp-field">
        <label>Druckzahl</label>
        <input type="number" min="0" step="1" v-model.number="form.stueck" placeholder="0" required />
      </div>
      <div class="sp-field">
        <label>Kürzel</label>
        <input type="text" maxlength="8" v-model="form.employee_kuerzel" placeholder="AK" />
      </div>
      <div class="sp-field">
        <label>Quelle</label>
        <select v-model="form.source">
          <option value="pc">PC</option>
          <option value="tablet">Tablet</option>
        </select>
      </div>
      <button class="sp-add" type="submit" :disabled="saving || !validEntry">
        <i class="fas fa-plus"></i> Erfassen
      </button>
    </form>
    <p v-if="error" class="sp-error">{{ error }}</p>

    <!-- Endlos-Protokoll (jüngste zuerst) -->
    <div class="sp-list">
      <div v-if="loading" class="sp-empty">Lädt …</div>
      <div v-else-if="!entries.length" class="sp-empty">Noch keine Schicht-Erfassung.</div>
      <table v-else class="sp-table">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Schicht</th>
            <th class="num">Druckzahl</th>
            <th>Kürzel</th>
            <th>Quelle</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in entries" :key="e.id">
            <td>{{ fmtDate(e.datum) }}</td>
            <td>{{ schichtLabel(e.schicht) }}</td>
            <td class="num">{{ fmt(e.stueck) }}</td>
            <td>{{ e.employee_kuerzel || '–' }}</td>
            <td><span class="sp-src" :class="e.source">{{ e.source === 'tablet' ? 'Tablet' : 'PC' }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'ShiftProductionPanel',
  props: {
    orderId: { type: [Number, String], required: true }
  },
  data() {
    return {
      entries: [],
      producedToday: 0,
      producedTotal: 0,
      loading: false,
      saving: false,
      error: '',
      form: this.blankForm()
    }
  },
  computed: {
    validEntry() {
      return this.form.datum && Number(this.form.stueck) >= 0
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    blankForm() {
      return {
        datum: new Date().toISOString().slice(0, 10),
        schicht: null,
        stueck: null,
        employee_kuerzel: '',
        source: 'pc'
      }
    },
    fmt(v) {
      return new Intl.NumberFormat('de-DE').format(Number(v) || 0)
    },
    fmtDate(s) {
      if (!s) return ''
      const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/)
      return m ? `${m[3]}.${m[2]}.${m[1]}` : s
    },
    schichtLabel(s) {
      if (s === 1) return '1 (06–14)'
      if (s === 2) return '2 (14–24)'
      return '1-Schicht'
    },
    async load() {
      if (!this.orderId) return
      this.loading = true
      try {
        const res = await api.get(`orders/${this.orderId}/shift_productions`)
        this.entries = res.data || []
        this.producedToday = res.produced_today || 0
        this.producedTotal = res.produced_total || 0
      } catch (e) {
        this.error = 'Protokoll konnte nicht geladen werden.'
      } finally {
        this.loading = false
      }
    },
    async submit() {
      if (!this.validEntry || this.saving) return
      this.saving = true
      this.error = ''
      try {
        const res = await api.post(`orders/${this.orderId}/shift_productions`, {
          shift_production: {
            datum: this.form.datum,
            schicht: this.form.schicht,
            stueck: Number(this.form.stueck) || 0,
            employee_kuerzel: this.form.employee_kuerzel,
            source: this.form.source
          }
        })
        if (res.data) this.entries.unshift(res.data)
        this.producedToday = res.produced_today ?? this.producedToday
        this.producedTotal = res.produced_total ?? this.producedTotal
        this.form = this.blankForm()
        this.$emit('updated', { producedToday: this.producedToday, producedTotal: this.producedTotal })
      } catch (e) {
        this.error = 'Erfassung fehlgeschlagen.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.shift-panel {
  border: 1px solid #e1e6ec;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}
.sp-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: #f6f8fa;
  border-bottom: 1px solid #e1e6ec;
  font-size: 0.86rem;
  font-weight: 700;
  color: #1f2328;
}
.sp-head i { color: #15803d; }
.sp-totals { margin-left: auto; font-weight: 400; color: #57606a; font-size: 0.8rem; }
.sp-totals strong { color: #15803d; }

.sp-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef0f3;
}
.sp-field { display: flex; flex-direction: column; gap: 3px; }
.sp-field label { font-size: 0.7rem; color: #6b7280; font-weight: 600; }
.sp-field input, .sp-field select {
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 0.84rem;
}
.sp-field input[type="number"] { width: 100px; }
.sp-field input[type="text"] { width: 80px; }
.sp-add {
  background: #15803d;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}
.sp-add:disabled { opacity: 0.5; cursor: not-allowed; }
.sp-add:hover:not(:disabled) { background: #166534; }
.sp-error { color: #b91c1c; font-size: 0.8rem; margin: 0; padding: 0 14px 8px; }

.sp-list { padding: 8px 14px 14px; max-height: 260px; overflow: auto; }
.sp-empty { color: #6b7280; font-size: 0.84rem; padding: 8px 0; }
.sp-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
.sp-table th {
  text-align: left;
  color: #57606a;
  font-weight: 600;
  border-bottom: 1px solid #e1e6ec;
  padding: 6px 8px;
}
.sp-table td { padding: 6px 8px; border-bottom: 1px solid #f1f3f5; color: #24292f; }
.sp-table .num { text-align: right; font-variant-numeric: tabular-nums; }
.sp-src {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e2e8f0;
  color: #475569;
}
.sp-src.tablet { background: #dbeafe; color: #1d4ed8; }
</style>
